import {
  flow,
  get,
  getOr,
  identity,
  isEqual,
  join,
  map,
  pickBy,
  snakeCase,
  toArray,
  toUpper,
} from 'lodash/fp';
import api from 'store/actions/api';

import { NOT_FOUND } from 'http-status';
import { LOCALE } from 'helpers/i18n';
import cmsLocaleMapping from 'helpers/cmsLocaleMapping';
import { createDuck, isLoadingDone } from './duckTools';
import { parseJSON } from './safetyJSON';

const defaultSelector = flow(
  get(['result']),
  (result) => {
    const isValidLocale = flow(
      get(['locale']),
      isEqual(getOr(LOCALE, [LOCALE], cmsLocaleMapping)),
    )(result);

    return isValidLocale ? result : {}; // This is because of a bug in cms service. If requested for nl_BE/de_AT, and the page does not exist, it return the corresponding page for nl/de
  },
  ({ content, template }) => ({
    ...flow(parseJSON, pickBy(identity))(content),
    template,
  }),
);

export const createCmsDuck = ({
  page: defaultPage,
  query = {},
  path,
  resultSelector = defaultSelector,
  marketingCms = false,
} = {}) => {
  path = toArray(path);

  const name = flow(map(snakeCase), join('/'), toUpper)(path);

  const duck = createDuck(
    name,
    {
      error: null,
      isLoaded: false,
      isLoading: false,
      result: null,
    },
    {
      loaders: {
        load: {
          fetch: (page = defaultPage, params) =>
            marketingCms
              ? api.getMarketingCmsPage(page, query)
              : api.getCmsPage(page, params),
          resultSelector,
        },
      },
    },
  );

  duck.tryLoad = (...args) =>
    function* ({ getState }) {
      const cms = get(path, getState());

      if (!isLoadingDone(cms)) {
        try {
          yield duck.load(...args);
        } catch (e) {
          if (e.statusCode !== NOT_FOUND) {
            throw e;
          }
        }
      }
    };

  return duck;
};

export const getBreadcrumbsMetaFromCms = (
  cmsPath = [],
  transformerFn = identity,
) =>
  flow(
    getOr([], [...cmsPath, 'result', 'breadcrumbs-meta']),
    map(({ title: text, ...rest }) => transformerFn({ text, ...rest })),
  );
